import { Tooltip } from '@monorepo/shared/componentsV2/Tooltip';
import { useIsTruncated } from '@monorepo/shared/hooks/useIsTruncated';
import {
  ComplianceCalendarEvent,
  ComplianceCalendarEventType,
} from 'mapistry-shared';
import React, { ReactElement } from 'react';
import { isEventClickable } from './helpers/complianceCalendarHelpers';

type CalendarEventTooltipProps = {
  event: Pick<ComplianceCalendarEvent, 'status' | 'title' | 'type'>;
  /**
   * Attach titleRef to the truncated title;
   * the returned element must accept a ref (see Tooltip).
   */
  children: (titleRef: (node: HTMLDivElement | null) => void) => ReactElement;
};

export function CalendarEventTooltip({
  event,
  children,
}: CalendarEventTooltipProps) {
  const [titleRef, isTitleTruncated] = useIsTruncated();
  let unclickableText;
  if (!isEventClickable(event.status)) {
    if (event.type === ComplianceCalendarEventType.INSPECTION) {
      unclickableText = 'Inspection not accessible until its period begins.';
    } else {
      unclickableText = "This event hasn't started yet.";
    }
  }

  // Always render Tooltip (an empty title never opens) so the child isn't
  // remounted, and loses focus, when truncation is measured after mount.
  return (
    <Tooltip
      title={
        isTitleTruncated || unclickableText ? (
          <>
            {isTitleTruncated && event.title}
            {isTitleTruncated && unclickableText && <br />}
            {unclickableText}
          </>
        ) : (
          ''
        )
      }
      tabIndex={0}
      placement="bottom-start"
    >
      {children(titleRef)}
    </Tooltip>
  );
}
